const tintCache = new Map();
const qrIconPositionCache = new Map();

export function createTintedLogoDataUrl(imageSource, accentColor) {
  const cacheKey = `${imageSource}|${accentColor.toLowerCase()}`;
  if (tintCache.has(cacheKey)) return tintCache.get(cacheKey);

  const pending = loadImage(imageSource).then((image) => {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) throw new Error("The browser could not tint the identity logo.");
    context.drawImage(image, 0, 0);
    const pixels = context.getImageData(0, 0, canvas.width, canvas.height);
    const [red, green, blue] = accentColor.slice(1).match(/.{2}/g).map((part) => parseInt(part, 16));
    for (let index = 0; index < pixels.data.length; index += 4) {
      if (pixels.data[index + 3] === 0) continue;
      pixels.data[index] = red;
      pixels.data[index + 1] = green;
      pixels.data[index + 2] = blue;
    }
    context.putImageData(pixels, 0, 0);
    return canvas.toDataURL("image/png");
  }).catch((error) => {
    tintCache.delete(cacheKey);
    throw error;
  });

  tintCache.set(cacheKey, pending);
  return pending;
}

export function createOpticallyCenteredQrIconDataUrl(imageSource, qrSize, imageSize) {
  const cacheKey = `${imageSource}|${qrSize}|${imageSize}`;
  if (qrIconPositionCache.has(cacheKey)) return qrIconPositionCache.get(cacheKey);

  const pending = loadImage(imageSource).then((image) => {
    const canvas = document.createElement("canvas");
    canvas.width = image.naturalWidth;
    canvas.height = image.naturalHeight;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("The browser could not position the QR icon.");

    const renderedOffset = Math.min(10, Math.max(6, qrSize * .0125));
    const sourceOffset = renderedOffset * image.naturalWidth / (qrSize * imageSize);
    context.drawImage(image, 0, sourceOffset, image.naturalWidth, image.naturalHeight);
    return canvas.toDataURL("image/png");
  }).catch((error) => {
    qrIconPositionCache.delete(cacheKey);
    throw error;
  });

  qrIconPositionCache.set(cacheKey, pending);
  return pending;
}

function loadImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("An identity logo asset could not be loaded."));
    image.src = source;
  });
}
